import type { WorkflowField } from '@/api/workflows';

/**
 * The HTML `autocomplete` token for a field, derived from what the originator
 * already authored (WCAG 2.1 — 1.3.5 Identify Input Purpose, Level AA, new in
 * 2.1).
 *
 * 1.3.5 asks that inputs collecting the USER'S OWN information expose their
 * purpose programmatically, so user agents and assistive tech can autofill or
 * re-present them with familiar icons and labels. It matters most for people
 * with motor or cognitive impairments, for whom retyping an address on every
 * form is the barrier.
 *
 * Derived rather than authored on purpose. Adding a per-field dropdown would
 * mean every existing seed and every future form has to remember to set it, and
 * the ones that forget fail silently. `addressRole` and `validate` are already
 * authored for other reasons and carry exactly the semantics 1.3.5 wants.
 *
 * IMPORTANT SCOPE LIMIT: the criterion applies only to fields about the person
 * filling the form. Our advisors enter CLIENT data — a beneficiary's postal
 * code is not the advisor's own — so browser autofill would be actively wrong
 * there. We therefore emit the token (which is what the criterion asks for, and
 * what AT reads) while leaving the browser's own heuristics to do what they
 * will; where a form genuinely captures the advisor's own details, the
 * behaviour is also correct.
 */
const ADDRESS_ROLE_TOKENS: Record<string, string> = {
  street: 'address-line1',
  apt: 'address-line2',
  city: 'address-level2',
  // Canadian provinces are the level-1 administrative division.
  province: 'address-level1',
  postal: 'postal-code',
};

const VALIDATE_TOKENS: Record<string, string> = {
  email: 'email',
  phone: 'tel',
  postal: 'postal-code',
};

export function autocompleteFor(field: WorkflowField): string | undefined {
  // Address parts are the strongest signal — the originator tagged them
  // explicitly so Canada Post could cross-fill siblings.
  if (field.addressRole) {
    const token = ADDRESS_ROLE_TOKENS[field.addressRole];
    if (token) return token;
  }
  if (field.validate) {
    const token = VALIDATE_TOKENS[field.validate];
    if (token) return token;
  }
  // A SIN is sensitive and has no autocomplete token in the spec; deliberately
  // left off rather than mapped to something approximate.
  return undefined;
}
